import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import AccommodationIndividualSelectRoomOptionTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTile'
import AccommodationIndividualSelectRoomOptionTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileDetails'
import AccommodationIndividualSelectRoomOptionTilePricing from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTilePricing'
import { useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import AccommodationIndividualSelectRoomOptionTileCTA from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileCTA'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'
import AccommodationIndividualSelectRoomOptionTileMessage from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileMessage'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import FlashHotelIndividualRoomsPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelIndividualRoomsPriceDetails'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import MultipleRoomsInclusions from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsInclusions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import GeoContext from 'contexts/geoContext'
import { AccommodationIndividualSelectRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileSubgrid'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import SuggestedPackagePromotionLabel from 'components/OfferPage/SuggestedPackagePromotionLabel'
import { useIntl } from 'react-intl'
import AirportTransferOption from 'components/OfferPage/FlashHotel/SameRoom/AirportTransferOption'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  showHotelOnly: boolean
  packageNumber: number
  packageView: App.OfferPackageOptionView
  displayPackageView?: App.OfferPackageOptionView
  checkInDate?: string
  checkOutDate?: string
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  rateListId?: string
  roomListId?: string
  currentRoom: OfferPageRoom
  onOptionSelect: (packageOption: App.HotelPackage) => void
  flightsEnabled: boolean
  isFlightsSelected: boolean
  variant: 'stacked' | 'carousel'
  hasAvailability: boolean
  inclusionsOpen?: boolean
  onInclusionsToggle?: () => void
  suggestedPackageMessage?: string
  airportTransferPriceDifference?: number
  airportTransferInclusion?: App.PackageInclusion
  airportTransferBasePackageKey?: string
  airportTransferSelected?: boolean
  onAirportTransferToggle?: (packageKey: string, event: React.ChangeEvent<HTMLInputElement>) => void
}

function IndividualRoomSelectRoomOption(props: Props) {
  const {
    showHotelOnly,
    packageNumber,
    packageView,
    displayPackageView = packageView,
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    rateListId: _rateListId,
    roomListId: _roomListId,
    currentRoom,
    onOptionSelect,
    flightsEnabled,
    isFlightsSelected,
    hasAvailability,
    variant,
    inclusionsOpen,
    onInclusionsToggle,
    suggestedPackageMessage,
    airportTransferPriceDifference,
    airportTransferInclusion,
    airportTransferBasePackageKey,
    airportTransferSelected,
    onAirportTransferToggle,
  } = props

  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const { getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const {
    totalFlightPrice,
    rewardAvailable,
    rewardSeatPointsPerPerson,
    rewardSeatCashCoPayPerPerson,
  } = useOfferPageFlightPrice()
  const roomPax = sumUpOccupancies([currentRoom])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  const roomSeatEligiblePax = (currentRoom.adults ?? 0) + (currentRoom.children ?? 0) + (currentRoom.teenagers ?? 0)
  const rewardPointsForRoom = rewardSeatPointsPerPerson != null
    ? rewardSeatPointsPerPerson * roomSeatEligiblePax
    : undefined
  const rewardCashCoPayForRoom = rewardSeatCashCoPayPerPerson != null
    ? rewardSeatCashCoPayPerPerson * roomSeatEligiblePax
    : undefined
  const isReward = !!rewardAvailable && rewardPointsForRoom !== undefined && rewardCashCoPayForRoom !== undefined

  const isSelected = packageView.package.id === currentRoom.roomId && packageView.package.roomRate.id === currentRoom.rateId

  const onSelect = useCallback(() => {
    onOptionSelect(packageView.package)
  }, [onOptionSelect, packageView.package])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, flightPrice: totalFlightPrice, skip: !isFlightsSelected || isReward }),
  ], [offer, packageView, isFlightsSelected, isReward, currentRegionCode, totalFlightPrice])

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = displayPackageView.package?.ignoreDynamicCancellationPolicy ?? false

  const selectedLabel = intl.formatMessage({
    id: '1IRx8lbZVkU7PYtgPvtT',
    defaultMessage: 'Selected',
    description: 'Button label indicating this room option is currently selected',
  })
  const selectLabel = intl.formatMessage({
    id: 'p_umY21zQsl1hqz1rtlE',
    defaultMessage: 'Select',
    description: 'Button label to select this room option on the hotel or room booking page',
  })
  const showAirportTransferOption = typeof airportTransferPriceDifference === 'number' && !!airportTransferInclusion && !!airportTransferBasePackageKey && !!onAirportTransferToggle
  const suggestedPackagePromotionLabel = suggestedPackageMessage && isRebrand
    ? <SuggestedPackagePromotionLabel message={suggestedPackageMessage} />
    : undefined

  return <AccommodationIndividualSelectRoomOptionTileSubgrid>
    {suggestedPackageMessage && !isRebrand && <MessageBanner kind="promotional" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>}
    <AccommodationIndividualSelectRoomOptionTile isSelected={isSelected}>
      <AccommodationIndividualSelectRoomOptionTileDetails
        variant={variant}
        roomPackage={displayPackageView.package}
        packageNumber={packageNumber}
        promotionLabel={suggestedPackagePromotionLabel}
        cancellationPolicy={<>
          {offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
            offerId={offer.id}
            useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
            timezoneOffset={offer.property?.timezoneOffset}
            uniqueKey={displayPackageView.package.uniqueKey}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            occupants={[currentRoom]}
            cancellationPolicy={displayPackageView.package?.roomRate?.cancellationPolicy}
            offerType={offer.type}
            showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView.package)}
          />}
          {offer.type === OFFER_TYPE_HOTEL && <FlashCancellationPolicies
            checkInDate={checkInDate}
            // Multi-room is not used on charter routes; pass explicitly so a leftover cart
            // departure date is never applied on the offer page.
            charterDepartDate={undefined}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={displayPackageView.package.roomRate?.cancellationPolicy}
            numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
            showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView.package)}
          />}
        </>}
      >
        <MultipleRoomsInclusions
          offer={offer}
          packageOption={displayPackageView}
          room={currentRoom}
          open={inclusionsOpen}
          onToggle={onInclusionsToggle}
        />
        {showAirportTransferOption && (
          <AirportTransferOption
            checked={airportTransferSelected}
            inclusion={airportTransferInclusion}
            packageKey={airportTransferBasePackageKey}
            onChange={onAirportTransferToggle}
            priceDifference={airportTransferPriceDifference}
          />
        )}
        <LoyaltyPointsWrapper>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="exact"
          />
        </LoyaltyPointsWrapper>
      </AccommodationIndividualSelectRoomOptionTileDetails>

      <WalledContent
        offerType={offer.type}
        enforced={offer.walledGarden}
        wall={<AccommodationIndividualSelectRoomOptionTilePricing>
          {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
            {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>{selectedLabel}</TextButton>}
            {!isSelected && <TextButton kind="secondary" onClick={onSelect}>{selectLabel}</TextButton>}
          </AccommodationIndividualSelectRoomOptionTileCTA>}

          {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
            <MessageBanner kind="warning" density="compact">
              {intl.formatMessage(
                {
                  id: 'hCs5ww5VP3AZQ_BQ-ry2',
                  defaultMessage: 'No {saleUnit} left',
                  description: 'Warning message when no rooms/units of the sale type are available',
                },
                { saleUnit: getSaleUnitPluralNoun(offer.saleUnit) },
              )}
            </MessageBanner>
          </AccommodationIndividualSelectRoomOptionTileMessage>}
        </AccommodationIndividualSelectRoomOptionTilePricing>}
      >
        <AccommodationIndividualSelectRoomOptionTilePricing>
          <FlashHotelIndividualRoomsPriceDetails
            room={currentRoom}
            packageView={packageView}
            hasFlights={flightsEnabled && isFlightsSelected}
            flightPrice={flightPrice}
            showChangeAirport={false}
            rewardAvailable={rewardAvailable}
            rewardPointsForRoom={rewardPointsForRoom}
            rewardCashCoPayForRoom={rewardCashCoPayForRoom}
          />
        </AccommodationIndividualSelectRoomOptionTilePricing>

        {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
          {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>{selectedLabel}</TextButton>}
          {!isSelected && <TextButton kind="secondary" onClick={onSelect}>{selectLabel}</TextButton>}
        </AccommodationIndividualSelectRoomOptionTileCTA>}

        {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
          <MessageBanner kind="warning" density="compact">
            {intl.formatMessage(
              {
                id: 'hCs5ww5VP3AZQ_BQ-ry2',
                defaultMessage: 'No {saleUnit} left',
                description: 'Warning message when no rooms/units of the sale type are available',
              },
              { saleUnit: getSaleUnitPluralNoun(offer.saleUnit) },
            )}
          </MessageBanner>
        </AccommodationIndividualSelectRoomOptionTileMessage>}
      </WalledContent>
    </AccommodationIndividualSelectRoomOptionTile>
  </AccommodationIndividualSelectRoomOptionTileSubgrid>
}

type MappedProps = Pick<Props,
  | 'showHotelOnly'
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'flightsEnabled'
  | 'isFlightsSelected'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  showHotelOnly: getShouldQualifyOfferHotelCancellation(state),
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
}), IndividualRoomSelectRoomOption)
